import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonToolbar,
  Card,
  Col,
  Form,
  InputGroup,
  ListGroup,
  Modal,
  Nav,
  Row,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import Content from "../../../components/Content";
import SubmitButton from "../../../components/SubmitButton";
import { capitalize } from "../../../lib/display";
import Loading from "../../../Loading";
import {
  ConnectedRow,
  REVIEW_QUERY_KEY,
  REVIEW_ROW_LIMIT,
  SAVE_STATUS_QUERY_KEY,
  ReviewGroup,
  ReviewHousehold,
  ReviewPair,
  ReviewRow,
  ReviewSave,
  ReviewSaveResult,
  ReviewSlantHousehold,
  SAVE_STATUS_POLL_MS,
  apiMessage,
  isSaveRunning,
  useHouseholdMappingReview,
  useReviewSaveStatus,
  useSaveReview,
} from "./api";
import type { SlantLinkHeld } from "../../../../../api/src/slant/slant-contact-link";
import {
  SlantLinkHeldAlert,
  slantLinksHeld,
} from "../../contact/SlantLinkHeld";
import {
  HouseholdReviewRow,
  HouseholdSummary,
  SlantHouseholdSummary,
  SlantReviewRow,
} from "./ReviewRows";

/**
 * One change the advisor made and did not save yet. `key` is the row the
 * change belongs to, so a row has at most one change.
 */
export type Change =
  | { kind: "connect"; key: string; pair: ReviewPair }
  | {
      kind: "create";
      key: string;
      slantHousehold: ReviewSlantHousehold;
      rejectedHouseholdIds: number[];
      /** Set when the advisor rejected the candidates of the row first. */
      row?: ReviewRow;
      fromGroup?: "clearMatch" | "possibleMatches";
    }
  | {
      // "None of these": the row leaves `fromGroup` and shows in "No match".
      // Save writes nothing for it.
      kind: "reject";
      key: string;
      row: ReviewRow;
      fromGroup: "clearMatch" | "possibleMatches";
    }
  | {
      kind: "disconnect";
      key: string;
      household: ReviewHousehold;
    };

const GROUPS: { group: ReviewGroup; label: string; description: string }[] = [
  {
    group: "clearMatch",
    label: "One clear match",
    description:
      "One AdviceCloud household shares people with the Slant household. Accept it, or choose a different household.",
  },
  {
    group: "possibleMatches",
    label: "Possible matches",
    description:
      "More than one household could be the match, or only the household name is similar. Pick one.",
  },
  {
    group: "noMatch",
    label: "No match",
    description:
      "No AdviceCloud household shares a person or has a similar name.",
  },
  {
    group: "connected",
    label: "Already connected",
    description: "Households connected to Slant.",
  },
];

/** The help article of this page (`api/help-content/clients/household-data-connections.md`). */
export const HELP_ARTICLE_PATH = "/help/clients/household-data-connections";

/** "3 households", "1 household". */
function households(count: number): string {
  return `${count} household${count === 1 ? "" : "s"}`;
}

/** What the page shows while a Save job waits or runs. */
export const SAVING_MESSAGE = "Saving. This can take up to five minutes.";

/** "3 connected, 0 disconnected, 2 created". */
function resultText(result: ReviewSaveResult): string {
  return `${result.connected} connected, ${result.disconnected} disconnected, ${result.created} created`;
}

/**
 * " 2 contacts added." after the counts of a result, or "" when the connects
 * added no contact (R116). A result of a job that an earlier worker finished
 * has no `contactsAdded`.
 */
function contactsAddedText(result: ReviewSaveResult): string {
  const count = result.contactsAdded ?? 0;
  return count > 0 ? ` ${count} contact${count === 1 ? "" : "s"} added.` : "";
}

/**
 * Each Slant person that a connect did not add, because a different contact
 * has its Slant link (R117, D16).
 */
const ContactsNotAdded = ({ result }: { result: ReviewSaveResult }) => {
  const people = result.contactsNotAdded ?? [];
  if (people.length === 0) {
    return null;
  }
  return (
    <ul className="mb-0 mt-2" data-testid="contacts-not-added">
      {people.map((person) => (
        <li key={person.slantContactId}>
          {person.name} was not added. A different contact has the Slant link of
          this person.
        </li>
      ))}
    </ul>
  );
};

/**
 * Whether the filter keeps `row`: the same names the api's filter reads, the
 * Slant household's and its people's, and each offered household's and its
 * people's, without regard to case.
 */
function matchesFilter(row: ReviewRow, filter: string): boolean {
  const text = filter.trim().toLowerCase();
  if (text === "") return true;
  return [
    row.slantHousehold.name,
    ...row.slantHousehold.people,
    ...row.candidates.flatMap((pair) => [
      pair.household.name,
      ...pair.household.people,
    ]),
  ].some((value) => value.toLowerCase().includes(text));
}

/**
 * The result of a completed save job (owner decision 8). A save that stopped
 * at a failed create leads with "Save stopped", names the create that failed,
 * and counts the creates it did not attempt. A create that a held Slant
 * person refused gives the reason of the job, which names the holder and
 * what to do (APP-1706, doc-held-slant-contact-links-01a11cd9686f430c, R12,
 * R13).
 */
const SaveResultAlert = ({
  result,
  onClose,
}: {
  result: ReviewSaveResult;
  onClose: () => void;
}) => {
  // The create that stopped the job. A create skipped as "already
  // connected" before it is not a failure, so it is not the first entry
  // always.
  const failed = result.notCreated.find(
    (household) =>
      household.reason === "BridgeFT failed" ||
      household.reason === "Slant person linked to a different contact",
  );
  const stopped = result.stopReason !== null && failed !== undefined;
  const savedAny =
    result.connected > 0 || result.disconnected > 0 || result.created > 0;
  const notAttempted = result.notCreated.filter(
    (household) => household.reason === "not attempted",
  ).length;
  const alreadyConnected = result.notCreated.filter(
    (household) => household.reason === "already connected",
  );
  return (
    <Alert
      variant={result.notCreated.length > 0 ? "warning" : "success"}
      data-testid="save-result"
      dismissible
      onClose={onClose}
    >
      {stopped ? (
        <>
          <div className="fw-semibold">
            Save stopped: {failed.reason}.{" "}
            {households(result.notCreated.length)} not created.
          </div>
          {savedAny && (
            <div>
              Saved before it stopped: {resultText(result)}.
              {contactsAddedText(result)}
            </div>
          )}
          <ContactsNotAdded result={result} />
          <div className="mt-2">
            {failed.name}: {failed.reason}.
            {notAttempted > 0 &&
              ` ${notAttempted} other${notAttempted === 1 ? "" : "s"} not attempted.`}
          </div>
          {alreadyConnected.length > 0 && (
            <div>
              {alreadyConnected
                .map((household) => `${household.name} (already connected)`)
                .join("; ")}
              .
            </div>
          )}
          {failed.reason === "BridgeFT failed" ? (
            <div>
              The households not created are still in No match. Create them
              again when BridgeFT is back.
            </div>
          ) : (
            <div data-testid="save-result-held">{result.stopReason}</div>
          )}
        </>
      ) : (
        <>
          <div>
            Saved: {resultText(result)}.{contactsAddedText(result)}
          </div>
          <ContactsNotAdded result={result} />
          {result.notCreated.length > 0 && (
            <>
              <div className="mt-2">
                {households(result.notCreated.length)} not created:
              </div>
              <ul className="mb-0">
                {result.notCreated.map((household) => (
                  <li key={household.slantHouseholdId}>
                    {household.name} ({household.reason})
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </Alert>
  );
};

/** How long ago `isoTime` was, in words. */
function ageOf(isoTime: string, now: number): string {
  const minutes = Math.floor((now - new Date(isoTime).getTime()) / 60_000);
  if (minutes < 1) return "less than a minute ago";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hour${hours === 1 ? "" : "s"} ago`;
}

/**
 * How many rows the list shows of how many: "50 of 241 Slant households" when
 * the list shows only the first rows, "3 Slant households" when it shows all.
 */
const RowTotal = ({
  shown,
  total,
  noun,
}: {
  shown: number;
  total: number;
  noun: string;
}) =>
  total === 0 ? null : (
    <div className="text-secondary small text-end mt-2">
      {shown < total ? `${shown} of ${total}` : total} {noun}
      {total === 1 ? "" : "s"}
    </div>
  );

/**
 * The household mapping page for Slant as a review list (APP-1692). The api
 * compares the Slant households with the firm's households when the page
 * opens; the advisor reviews the four groups and saves. Nothing is written
 * until Save.
 */
const HouseholdMappingReview = ({
  providerOptions,
  onSetService,
}: {
  providerOptions: string[];
  /** Report a different CRM picked in the CRM select. */
  onSetService: (service: string) => void;
}) => {
  const [group, setGroup] = useState<ReviewGroup>("clearMatch");
  const [filterText, setFilterText] = useState("");
  const [filter, setFilter] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilter(filterText.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [filterText]);

  // "Show archived households": off each time the page opens, and not saved (R107).
  const [includeArchived, setIncludeArchived] = useState(false);

  const review = useHouseholdMappingReview({ group, filter, includeArchived });
  const data = review.data?.data;
  // While another group loads, the query keeps the rows of the group shown
  // before (`keepPreviousData`). Those rows are not rows of `group`: none is
  // shown, so no action of `group` (Accept, Accept all) acts on them.
  const isGroupLoading = data !== undefined && data.group !== group;
  const groupData = isGroupLoading ? undefined : data;

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);
  // The age is read against the time of the answer, not the last tick.
  useEffect(() => setNow(Date.now()), [data?.comparedAt]);

  const [changes, setChanges] = useState<Record<string, Change>>({});
  const changeList = useMemo(() => Object.values(changes), [changes]);
  const changeCount = changeList.length;
  const setChange = useCallback((change: Change) => {
    setChanges((previous) => ({ ...previous, [change.key]: change }));
  }, []);
  const undoChange = useCallback((key: string) => {
    setChanges((previous) => {
      const { [key]: _removed, ...rest } = previous;
      return rest;
    });
  }, []);

  // A household and a Slant household can each be in one connect only.
  const householdsInChanges = useMemo(() => {
    const ids = new Map<number, string>();
    for (const change of changeList) {
      if (change.kind === "connect") {
        ids.set(change.pair.household.id, change.key);
      }
    }
    return ids;
  }, [changeList]);
  const slantHouseholdsInChanges = useMemo(() => {
    const ids = new Map<string, string>();
    for (const change of changeList) {
      if (change.kind === "connect") {
        ids.set(change.pair.slantHousehold.id, change.key);
      } else if (change.kind === "create") {
        ids.set(change.slantHousehold.id, change.key);
      }
    }
    return ids;
  }, [changeList]);

  /** Whether `pair` can be accepted for the row `key`. */
  const canAccept = useCallback(
    (key: string, pair: ReviewPair) => {
      const householdKey = householdsInChanges.get(pair.household.id);
      const slantKey = slantHouseholdsInChanges.get(pair.slantHousehold.id);
      return (
        pair.alreadyMapped === null &&
        (householdKey === undefined || householdKey === key) &&
        (slantKey === undefined || slantKey === key)
      );
    },
    [householdsInChanges, slantHouseholdsInChanges],
  );
  /** Whether the Slant household can be created for the row `key`. */
  const canCreate = useCallback(
    (key: string, slantHouseholdId: string) => {
      const slantKey = slantHouseholdsInChanges.get(slantHouseholdId);
      return slantKey === undefined || slantKey === key;
    },
    [slantHouseholdsInChanges],
  );

  // The rows the advisor moved to "No match" with "None of these".
  const movedChanges = useMemo(
    () =>
      changeList.filter(
        (
          change,
        ): change is Extract<Change, { row?: ReviewRow }> & {
          row: ReviewRow;
          fromGroup: "clearMatch" | "possibleMatches";
        } =>
          (change.kind === "reject" || change.kind === "create") &&
          change.row !== undefined &&
          change.fromGroup !== undefined,
      ),
    [changeList],
  );
  const movedKeys = useMemo(
    () => new Set(movedChanges.map((change) => change.key)),
    [movedChanges],
  );
  // The moved rows the filter keeps, by the same names the api's filter reads.
  const filteredMovedChanges = useMemo(
    () => movedChanges.filter((change) => matchesFilter(change.row, filter)),
    [movedChanges, filter],
  );
  const rows: ReviewRow[] = useMemo(() => {
    const groupRows = (groupData?.rows ?? []).filter(
      (row) => !movedKeys.has(`slant:${row.slantHousehold.id}`),
    );
    return group === "noMatch"
      ? [
          ...filteredMovedChanges.map((change) => change.row),
          ...groupRows,
        ].slice(0, REVIEW_ROW_LIMIT)
      : groupRows;
  }, [groupData, group, filteredMovedChanges, movedKeys]);
  // The rows of the group after the filter, all of them: the page shows the
  // first `REVIEW_ROW_LIMIT`. Rows moved with "None of these" count in
  // "No match" and not in their group.
  const rowTotal = useMemo(() => {
    if (!groupData) return 0;
    const moved = filteredMovedChanges.filter(
      (change) => change.fromGroup === group,
    ).length;
    return (
      groupData.total -
      moved +
      (group === "noMatch" ? filteredMovedChanges.length : 0)
    );
  }, [groupData, group, filteredMovedChanges]);
  // The counts of the api for the filter, with the moved rows in "No match".
  // The api counts only the rows that match the filter, so only the moved
  // rows that match it move a count (R104). A moved row that then gets
  // "Create household" still counts in "No match" (owner decision 13).
  const counts = useMemo(() => {
    if (!data) return null;
    const from = (g: ReviewGroup) =>
      filteredMovedChanges.filter((change) => change.fromGroup === g).length;
    return {
      ...data.counts,
      clearMatch: data.counts.clearMatch - from("clearMatch"),
      possibleMatches: data.counts.possibleMatches - from("possibleMatches"),
      noMatch: data.counts.noMatch + filteredMovedChanges.length,
    };
  }, [data, filteredMovedChanges]);
  const acceptAllRows = useMemo(() => {
    if (group !== "clearMatch") return [];
    const taken = new Set<number>();
    return rows.filter((row) => {
      const key = `slant:${row.slantHousehold.id}`;
      const pair = row.candidates[0];
      if (!pair || changes[key] || !canAccept(key, pair)) return false;
      // Two rows of the group can offer one household: the first takes it.
      if (taken.has(pair.household.id)) return false;
      taken.add(pair.household.id);
      return true;
    });
  }, [group, rows, changes, canAccept]);
  const acceptAll = useCallback(() => {
    setChanges((previous) => {
      const next = { ...previous };
      for (const row of acceptAllRows) {
        const key = `slant:${row.slantHousehold.id}`;
        next[key] = { kind: "connect", key, pair: row.candidates[0] };
      }
      return next;
    });
  }, [acceptAllRows]);

  const creatableRows = useMemo(
    () =>
      group !== "noMatch"
        ? []
        : // Only rows with no candidate. A row moved here with "None of these"
          // is created with its own "Create household" (owner decision 4:
          // create stays a separate deliberate step).
          rows.filter(
            (row) =>
              row.candidates.length === 0 &&
              !changes[`slant:${row.slantHousehold.id}`] &&
              // Not in a connect of a household row ("Search Slant").
              !slantHouseholdsInChanges.has(row.slantHousehold.id),
          ),
    [group, rows, changes, slantHouseholdsInChanges],
  );
  const [showCreateAll, setShowCreateAll] = useState(false);
  const createAll = useCallback(() => {
    setChanges((previous) => {
      const next = { ...previous };
      for (const row of creatableRows) {
        const key = `slant:${row.slantHousehold.id}`;
        const rejected = previous[key];
        next[key] = {
          kind: "create",
          key,
          slantHousehold: row.slantHousehold,
          rejectedHouseholdIds: row.candidates.map((pair) => pair.household.id),
          ...(rejected?.kind === "reject"
            ? { row: rejected.row, fromGroup: rejected.fromGroup }
            : {}),
        };
      }
      return next;
    });
    setShowCreateAll(false);
  }, [creatableRows]);

  const queryClient = useQueryClient();
  const save = useSaveReview();
  // The id of the job that this tab's Save queued, in component state only
  // (owner decision 7: "'Saved: …' only in the browser tab that pressed
  // Save"). Null: the page reads only a waiting or active job of the firm, or
  // the user's own failed job.
  const [heldJobId, setHeldJobId] = useState<string | null>(null);
  const saveStatus = useReviewSaveStatus(SAVE_STATUS_POLL_MS, heldJobId);
  // While the POST is in flight or a Save job of the firm waits or runs, no
  // action adds, removes or saves a change. The page shows the same state
  // when it opens while a job of the firm runs.
  // Between the POST's answer and the first read of the held job, the
  // status query has no data yet: the page stays locked in that window too.
  const isSaving =
    save.isPending ||
    (heldJobId !== null &&
      saveStatus.data === undefined &&
      !saveStatus.isError) ||
    isSaveRunning(saveStatus.data);
  const [saveError, setSaveError] = useState<string | null>(null);
  // A Save that the api refused because a different contact holds a Slant
  // person of it (APP-1706, doc-held-slant-contact-links-01a11cd9686f430c).
  const [saveHeld, setSaveHeld] = useState<SlantLinkHeld[] | null>(null);
  const [saveResult, setSaveResult] = useState<ReviewSaveResult | null>(null);

  // Stop reading the held job, and read the firm again, so that a later job
  // of a different user locks the page. The answer the page kept for the
  // firm is from before this save, so it is dropped first.
  const readFirmAgain = useCallback(() => {
    queryClient.removeQueries({
      queryKey: [SAVE_STATUS_QUERY_KEY, null],
      exact: true,
    });
    setHeldJobId(null);
  }, [queryClient]);

  // The finished job the page showed last, so that it shows each one once.
  const shownJobId = useRef<string | null>(null);
  const status = saveStatus.data;
  useEffect(() => {
    if (
      status === undefined ||
      (status.state !== "completed" && status.state !== "failed") ||
      shownJobId.current === status.jobId
    ) {
      return;
    }
    const isHeld = status.jobId === heldJobId;
    if (status.state === "completed" && isHeld) {
      shownJobId.current = status.jobId;
      // The saved changes are in the database now: drop them, and read the
      // list again (`slantRead=kept`, no Slant request).
      setChanges({});
      setSaveError(null);
      setSaveHeld(null);
      setSaveResult(status.result);
      queryClient.invalidateQueries({ queryKey: [REVIEW_QUERY_KEY] });
      readFirmAgain();
    } else if (status.state === "failed" && (isHeld || status.mine)) {
      shownJobId.current = status.jobId;
      // The changes stay, so that the advisor can save again.
      setSaveResult(null);
      setSaveHeld(null);
      setSaveError(`The save failed: ${status.reason}`);
      if (isHeld) readFirmAgain();
    }
  }, [status, heldJobId, queryClient, readFirmAgain]);

  const onSave = useCallback(async () => {
    setSaveError(null);
    setSaveHeld(null);
    setSaveResult(null);
    const body: ReviewSave = { connect: [], create: [], disconnect: [] };
    for (const change of changeList) {
      if (change.kind === "connect") {
        body.connect.push({
          householdId: change.pair.household.id,
          slantHouseholdId: change.pair.slantHousehold.id,
        });
      } else if (change.kind === "create") {
        body.create.push({
          slantHouseholdId: change.slantHousehold.id,
          rejectedHouseholdIds: change.rejectedHouseholdIds,
        });
      } else if (change.kind === "disconnect") {
        body.disconnect.push(change.household.id);
      }
    }
    if (
      body.connect.length === 0 &&
      body.create.length === 0 &&
      body.disconnect.length === 0
    ) {
      // Only rejections: nothing to write.
      setChanges({});
      return;
    }
    try {
      // The api queues one job. The changes stay until the job completes.
      const { jobId } = await save.mutateAsync(body);
      setHeldJobId(jobId);
    } catch (error) {
      // A 409: a job of the firm runs. Read the firm, so the page locks.
      setHeldJobId(null);
      console.error("Failed to save household data connections", error);
      const held = slantLinksHeld(error);
      if (held !== null) {
        setSaveHeld(held);
        return;
      }
      setSaveError(
        apiMessage(error, "Failed to save the household data connections."),
      );
    }
  }, [changeList, save]);

  const revert = useCallback(() => {
    setChanges({});
    setSaveError(null);
    setSaveHeld(null);
  }, []);

  const selectGroup = useCallback((next: ReviewGroup) => {
    setGroup(next);
  }, []);

  const activeGroup =
    GROUPS.find((entry) => entry.group === group) ?? GROUPS[0];
  const connectedRows: ConnectedRow[] = groupData?.connectedRows ?? [];

  return (
    <>
      <h1>Household Data Connections</h1>
      <Content>
        <Row className="align-items-end mb-2">
          <Col md={8}>
            <p className="text-secondary mb-2">
              Each Slant household is compared with your AdviceCloud households
              when this page opens. Review the results, then save.{" "}
              <Link
                to={HELP_ARTICLE_PATH}
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </Link>
            </p>
          </Col>
          <Col xs="auto" className="ms-auto">
            <Form.Group
              controlId="form-provider"
              className="mb-2"
              style={{ maxWidth: "12rem" }}
            >
              <Form.Label>CRM</Form.Label>
              <Form.Select
                value="slant"
                onChange={(event) => onSetService(event.currentTarget.value)}
              >
                {providerOptions.map((providerOption) => (
                  <option key={providerOption} value={providerOption}>
                    {capitalize(providerOption)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {review.isPending ? (
          <Loading />
        ) : review.isError && !data ? (
          <Alert variant="danger">
            {apiMessage(
              review.error,
              "Failed to compare the Slant households with your households.",
            )}
          </Alert>
        ) : data ? (
          <>
            <div className="d-flex align-items-center justify-content-end mb-2">
              <span className="text-secondary small me-2">
                Compared {ageOf(data.comparedAt, now)}
              </span>
              <SubmitButton
                type="button"
                label="Compare again"
                className="btn-sm"
                isSubmitting={review.isComparingAgain}
                onClick={review.compareAgain}
              />
            </div>

            {review.compareFailure !== null && (
              <Alert variant="danger">
                {apiMessage(
                  review.compareFailure.error,
                  "Failed to compare the Slant households with your households.",
                )}
              </Alert>
            )}

            {data.slantReadMessage !== null && (
              <Alert variant="warning">
                {data.slantReadMessage} This page shows the comparison from{" "}
                {ageOf(data.comparedAt, now)}. You can still save.
              </Alert>
            )}

            {isSaveRunning(status) && (
              <Alert variant="info" data-testid="save-status">
                {SAVING_MESSAGE}
              </Alert>
            )}
            {saveResult !== null && (
              <SaveResultAlert
                result={saveResult}
                onClose={() => setSaveResult(null)}
              />
            )}

            <Nav
              variant="tabs"
              activeKey={group}
              onSelect={(key) => key && selectGroup(key as ReviewGroup)}
              // `flex-wrap` also below `lg`, where `.nav-tabs` is a row that
              // scrolls (`Nav.scss`) and would hide the filter at its end.
              className="mb-3 align-items-end flex-wrap"
              data-testid="group-tabs"
            >
              {GROUPS.map((entry) => (
                <Nav.Item key={entry.group}>
                  <Nav.Link eventKey={entry.group}>
                    {entry.label}{" "}
                    <Badge
                      bg="secondary"
                      pill
                      data-testid={`count-${entry.group}`}
                    >
                      {(counts ?? data.counts)[entry.group]}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
              ))}
              {/* After the tabs and to the left of the filter (R106). */}
              <Nav.Item className="ms-3 mb-2" data-testid="show-archived">
                <Form.Check
                  type="switch"
                  id="show-archived-households"
                  label="Show archived households"
                  checked={includeArchived}
                  onChange={(event) =>
                    setIncludeArchived(event.currentTarget.checked)
                  }
                />
              </Nav.Item>
              {/* The filter is the last item of the tab row, so the line under
                  the tabs runs under it too. Its flex basis is its smallest
                  width, 10rem, so the row wraps only when the tabs and 10rem do
                  not fit; on the row it grows to 20rem. At a window 1440 px
                  wide, with the sidebar, it stays on the row of the tabs. */}
              <Nav.Item
                className="ms-auto"
                data-testid="group-filter"
                style={{ flex: "1 1 10rem", maxWidth: "20rem" }}
              >
                <InputGroup size="sm" className="mb-1">
                  <InputGroup.Text>Filter</InputGroup.Text>
                  <Form.Control
                    type="text"
                    aria-label="Filter by household or person name"
                    placeholder="Household or person name"
                    value={filterText}
                    onChange={(event) =>
                      setFilterText(event.currentTarget.value)
                    }
                  />
                </InputGroup>
              </Nav.Item>
            </Nav>

            <Card className="mb-3">
              <Card.Header className="d-flex align-items-center justify-content-between">
                <span>{activeGroup.description}</span>
                {group === "clearMatch" && (
                  <Button
                    size="sm"
                    disabled={isSaving || acceptAllRows.length === 0}
                    onClick={acceptAll}
                  >
                    Accept all {acceptAllRows.length}
                  </Button>
                )}
                {group === "noMatch" && (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={isSaving || creatableRows.length === 0}
                    onClick={() => setShowCreateAll(true)}
                  >
                    Create {households(creatableRows.length)}…
                  </Button>
                )}
              </Card.Header>
              <ListGroup variant="flush">
                {isGroupLoading ? (
                  <ListGroup.Item>
                    <Loading />
                  </ListGroup.Item>
                ) : group === "connected" ? (
                  connectedRows.length === 0 ? (
                    <ListGroup.Item className="text-secondary">
                      No households.
                    </ListGroup.Item>
                  ) : (
                    connectedRows.map((row) => {
                      const key = `household:${row.household.id}`;
                      const change = changes[key];
                      return (
                        <ListGroup.Item key={key}>
                          <Row className="align-items-center">
                            <Col md={5}>
                              <HouseholdSummary household={row.household} />
                            </Col>
                            <Col md={5}>
                              {row.slantHousehold === null ? (
                                <span className="text-secondary">
                                  Slant household not found in Slant
                                </span>
                              ) : (
                                <>
                                  <div className="fw-semibold">
                                    {row.slantHousehold.name}
                                  </div>
                                  <div className="text-secondary small">
                                    Slant household · {row.connectedPeople} of{" "}
                                    {row.slantHousehold.people.length} people
                                    connected
                                  </div>
                                </>
                              )}
                            </Col>
                            <Col md={2} className="text-end">
                              {change ? (
                                <>
                                  <Badge bg="warning" text="dark">
                                    Will be disconnected
                                  </Badge>{" "}
                                  <Button
                                    variant="link"
                                    size="sm"
                                    disabled={isSaving}
                                    onClick={() => undoChange(key)}
                                  >
                                    Undo
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  variant="link"
                                  size="sm"
                                  disabled={isSaving}
                                  onClick={() =>
                                    setChange({
                                      kind: "disconnect",
                                      key,
                                      household: row.household,
                                    })
                                  }
                                >
                                  Disconnect
                                </Button>
                              )}
                            </Col>
                          </Row>
                        </ListGroup.Item>
                      );
                    })
                  )
                ) : rows.length === 0 ? (
                  <ListGroup.Item className="text-secondary">
                    No Slant households.
                  </ListGroup.Item>
                ) : (
                  rows.map((row) => {
                    const key = `slant:${row.slantHousehold.id}`;
                    return (
                      <SlantReviewRow
                        key={key}
                        rowKey={key}
                        row={row}
                        group={group}
                        change={changes[key]}
                        canAccept={canAccept}
                        canCreate={canCreate}
                        onChange={setChange}
                        onUndo={undoChange}
                        locked={isSaving}
                      />
                    );
                  })
                )}
              </ListGroup>
              {group === "clearMatch" && !isGroupLoading && rows.length > 0 && (
                <Card.Footer className="d-flex justify-content-end">
                  <Button
                    size="sm"
                    disabled={isSaving || acceptAllRows.length === 0}
                    onClick={acceptAll}
                  >
                    Accept all {acceptAllRows.length}
                  </Button>
                </Card.Footer>
              )}
            </Card>
            {!isGroupLoading && (
              <RowTotal
                noun={group === "connected" ? "household" : "Slant household"}
                shown={
                  group === "connected" ? connectedRows.length : rows.length
                }
                total={rowTotal}
              />
            )}

            {group === "noMatch" && (
              <>
                <Card className="mb-3 mt-3">
                  <Card.Header>
                    AdviceCloud households with no Slant household{" "}
                    <Badge
                      bg="secondary"
                      pill
                      data-testid="count-householdsWithNoSlantRecord"
                    >
                      {data.counts.householdsWithNoSlantRecord}
                    </Badge>
                    <div className="text-secondary small">
                      No Slant household shares a person or has a similar name.
                      Search Slant to connect one.
                    </div>
                  </Card.Header>
                  <ListGroup variant="flush">
                    {data.householdRows.length === 0 ? (
                      <ListGroup.Item className="text-secondary">
                        No households.
                      </ListGroup.Item>
                    ) : (
                      data.householdRows.map((household) => {
                        const key = `household:${household.id}`;
                        return (
                          <HouseholdReviewRow
                            key={key}
                            rowKey={key}
                            household={household}
                            change={changes[key]}
                            canAccept={canAccept}
                            onChange={setChange}
                            onUndo={undoChange}
                            locked={isSaving}
                          />
                        );
                      })
                    )}
                  </ListGroup>
                </Card>
                <RowTotal
                  noun="household"
                  shown={data.householdRows.length}
                  total={data.householdTotal}
                />
              </>
            )}

            {saveError !== null && (
              <Alert variant="danger" className="mt-3">
                {saveError}
              </Alert>
            )}
            {saveHeld !== null && (
              <div className="mt-3">
                <SlantLinkHeldAlert held={saveHeld} />
              </div>
            )}

            <Row className="mt-3">
              <Col className="d-flex align-items-center">
                <ButtonToolbar>
                  <SubmitButton
                    type="button"
                    className="me-2"
                    label={changeCount > 0 ? `Save (${changeCount})` : "Save"}
                    disabled={changeCount === 0 || isSaving}
                    isSubmitting={save.isPending}
                    onClick={onSave}
                  />
                  <Button
                    variant="danger"
                    disabled={changeCount === 0 || isSaving}
                    onClick={revert}
                  >
                    {changeCount > 0 ? `Revert (${changeCount})` : "Revert"}
                  </Button>
                </ButtonToolbar>
                <span className="text-secondary ms-3" data-testid="changes">
                  {changeCount === 0
                    ? "No changes to save"
                    : `${changeCount} change${changeCount === 1 ? "" : "s"} to save`}
                </span>
              </Col>
            </Row>
          </>
        ) : null}
      </Content>

      <Modal show={showCreateAll} onHide={() => setShowCreateAll(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create {households(creatableRows.length)}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Save creates {households(creatableRows.length)} in AdviceCloud, each
            with the people of its Slant household, and connects each to Slant.
            You cannot undo this from this page after you save.
          </p>
          <ListGroup variant="flush">
            {creatableRows.map((row) => (
              <ListGroup.Item key={row.slantHousehold.id}>
                <SlantHouseholdSummary slantHousehold={row.slantHousehold} />
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateAll(false)}>
            Cancel
          </Button>
          <Button onClick={createAll}>
            Create {households(creatableRows.length)}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default HouseholdMappingReview;
